import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ExistingReview {
  id: string;
  rating: number;
  comment: string | null;
}

export function useOrganizerReview(organizerId: string | null, eventId?: string) {
  const { user } = useAuth();
  const [existingReview, setExistingReview] = useState<ExistingReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user?.id || !organizerId) {
      setLoading(false);
      return;
    }

    const fetchReview = async () => {
      let query = supabase
        .from('organizer_reviews' as any)
        .select('id, rating, comment')
        .eq('organizer_id', organizerId)
        .eq('user_id', user.id);

      if (eventId) {
        query = query.eq('event_id', eventId);
      } else {
        query = query.is('event_id', null);
      }

      const { data, error } = await query.maybeSingle();

      if (!error && data) {
        setExistingReview(data as any);
      }
      setLoading(false);
    };

    fetchReview();
  }, [user?.id, organizerId, eventId]);

  const submitReview = async (rating: number, comment: string) => {
    if (!user?.id || !organizerId) {
      toast.error('No se pudo enviar la evaluación');
      return false;
    }

    setSubmitting(true);
    try {
      const reviewData: any = {
        organizer_id: organizerId,
        user_id: user.id,
        rating,
        comment: comment || null,
      };

      if (eventId) {
        reviewData.event_id = eventId;
      }

      let result;
      if (existingReview) {
        result = await supabase
          .from('organizer_reviews' as any)
          .update({ rating, comment: comment || null })
          .eq('id', existingReview.id)
          .select('id, rating, comment')
          .single();
      } else {
        result = await supabase
          .from('organizer_reviews' as any)
          .insert(reviewData)
          .select('id, rating, comment')
          .single();
      }

      if (result.error) throw result.error;

      setExistingReview(result.data as any);
      toast.success('¡Evaluación enviada! Gracias por tu opinión.');
      window.dispatchEvent(new Event('organizer-review-submitted'));
      return true;
    } catch (err) {
      console.error('Error submitting review:', err);
      toast.error('Error al enviar la evaluación');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    hasReviewed: !!existingReview,
    existingReview,
    loading,
    submitting,
    submitReview,
  };
}
